import React, { useEffect, useState } from 'react';
import {
    Snackbar,
    Alert,
    Tooltip
} from '@mui/material';
import { Check, Trash2, Heart, Users, Plus, AlertCircle, X, ShieldCheck } from 'lucide-react';

import { AllergyFormProps } from './Allergies';
import { BASE_URL } from '../../../components/interface/Config';
import { useAuth } from '../../../context/AuthContext';
import LoadingAnimation from '../../../components/LoadingAnimation';
import { apiHandler } from '../../../services/authService';

interface FamilyMember {
    relationship: string;
    status: boolean;
}

interface Disease {
    id: number;
    slug: string;
    name: string;
    attributes: {
        values: FamilyMember[];
    };
}

const FamilyHistory: React.FC<AllergyFormProps> = ({ patientId }) => {
    const { token } = useAuth();
    const [diseases, setDiseases] = useState<Disease[]>([]);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'warning' | 'error'>('success');
    const [loading, setLoading] = useState<boolean>(false);
    
    // Quick Add Form States
    const [selectedMember, setSelectedMember] = useState<string>('Father');
    const [selectedDiseaseId, setSelectedDiseaseId] = useState<number>(0);
    const [adding, setAdding] = useState<boolean>(false);

    const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const relationships = [
        'Grandparents',
        'Father',
        'Mother',
        'Brother(s)',
        'Sister(s)',
        'Daughter(s)',
        'Son(s)'
    ];

    useEffect(() => {
        const fetchDiseases = async () => {
            try {
                setLoading(true);

                const url = `${BASE_URL}/resource/masters/all?slug=family_history_diseases&order_by=id&dir=1&patient_id=${patientId}`;
                const response = await apiHandler('', 'GET', {}, url);

                if (!response) {
                    throw new Error("No response received from the server");
                }

                const data = response.data;
                if (data.code === 200) {
                    const updatedDiseases: Disease[] = data.data.masters.map((disease: any) => ({
                        id: disease.id,
                        name: disease.name,
                        slug: disease.slug,
                        attributes: {
                            values: disease.attributes.values.map((value: any) => ({
                                relationship: value.relationship,
                                status: value.status || false,
                            })),
                        },
                    }));
                    setDiseases(updatedDiseases);
                    
                    // Set initial selected disease to the first item by default if available
                    if (updatedDiseases.length > 0) {
                        setSelectedDiseaseId(updatedDiseases[0].id);
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                showSnackbar('Failed to fetch family history data', 'error');
            } finally {
                setLoading(false);
            }
        };

        fetchDiseases();
    }, [token, patientId]);

    const handleAddRecord = async () => {
        if (selectedDiseaseId === 0) {
            showSnackbar('Please select a valid medical condition', 'warning');
            return;
        }

        const targetDisease = diseases.find(d => d.id === selectedDiseaseId);
        if (!targetDisease) return;

        // Optimistic UI updates
        const originalDiseases = [...diseases];
        
        const newDiseases = diseases.map(disease => {
            if (disease.id === selectedDiseaseId) {
                const updatedValues = disease.attributes.values.map(val => {
                    if (val.relationship === selectedMember) {
                        return { ...val, status: true };
                    }
                    return val;
                });
                return {
                    ...disease,
                    attributes: {
                        ...disease.attributes,
                        values: updatedValues
                    }
                };
            }
            return disease;
        });

        setDiseases(newDiseases);

        // Update target payload representation
        const updatedDisease = newDiseases.find(d => d.id === selectedDiseaseId);
        if (!updatedDisease) return;

        try {
            setAdding(true);
            const token = sessionStorage.getItem("authToken") || "";
            const url = `${BASE_URL}/resource/familyHistories`;
            const payload = {
                slug: updatedDisease.slug,
                patient_id: patientId,
                details: selectedMember,
                status: false // Laravel expects status: false to insert details into the database array
            };
            
            await apiHandler(token, 'POST', payload, url);
            showSnackbar(`Successfully added ${updatedDisease.name} for ${selectedMember}`, 'success');
        } catch (error) {
            console.error('Error saving family history:', error);
            showSnackbar('Failed to add family history record', 'error');
            setDiseases(originalDiseases); // Revert state
        } finally {
            setAdding(false);
        }
    };

    const handleRemoveRecord = async (diseaseId: number, relationName: string) => {
        const targetDisease = diseases.find(d => d.id === diseaseId);
        if (!targetDisease) return;

        // Optimistic local state removal
        const originalDiseases = [...diseases];

        const newDiseases = diseases.map(disease => {
            if (disease.id === diseaseId) {
                const updatedValues = disease.attributes.values.map(val => {
                    if (val.relationship === relationName) {
                        return { ...val, status: false };
                    }
                    return val;
                });
                return {
                    ...disease,
                    attributes: {
                        ...disease.attributes,
                        values: updatedValues
                    }
                };
            }
            return disease;
        });

        setDiseases(newDiseases);

        const updatedDisease = newDiseases.find(d => d.id === diseaseId);
        if (!updatedDisease) return;

        try {
            const token = sessionStorage.getItem("authToken") || "";
            const url = `${BASE_URL}/resource/familyHistories`;
            const payload = {
                slug: updatedDisease.slug,
                patient_id: patientId,
                details: relationName,
                status: true // Laravel expects status: true to remove details from the database array
            };
            
            await apiHandler(token, 'POST', payload, url);
            showSnackbar(`Removed ${updatedDisease.name} for ${relationName}`, 'info');
        } catch (error) {
            console.error('Error removing record:', error);
            showSnackbar('Failed to remove history record', 'error');
            setDiseases(originalDiseases); // Revert state
        }
    };

    // Filter relationships that have at least one reported positive condition
    const activeMembers = relationships.filter(member => 
        diseases.some(d => d.attributes.values.some(v => v.relationship === member && v.status))
    );

    return (
        <div className="font-jost text-slate-700" style={{ fontFamily: 'Jost, sans-serif' }}>
            {loading ? (
                <div className="flex justify-center items-center py-20">
                    <LoadingAnimation />
                </div>
            ) : (
                <div className="space-y-6 mt-4">

                    {/* Quick Add Form Panel (Extremely user friendly, simple & fast) */}
                    <div className="bg-slate-50 border border-slate-100 rounded-3xl p-5 md:p-6 shadow-[0_8px_30px_rgba(8,112,180,0.015)]">
                        <div className="flex items-center gap-2 mb-4">
                            <Heart className="text-[#0870b4] w-4.5 h-4.5" />
                            <h3 className="text-sm font-extrabold text-slate-800 uppercase tracking-wider">Quick Add Family Record</h3>
                        </div>
                        
                        <div className="flex flex-col md:flex-row gap-4 items-end">
                            <div className="flex-1 w-full space-y-1.5">
                                <label className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 pl-1">Family Member</label>
                                <select 
                                    value={selectedMember} 
                                    onChange={e => setSelectedMember(e.target.value)}
                                    className="w-full bg-white border border-slate-100 rounded-xl px-4 py-2.5 text-xs sm:text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#0870b4] text-slate-700"
                                >
                                    {relationships.map(r => <option key={r} value={r}>{r}</option>)}
                                </select>
                            </div>

                            <div className="flex-grow w-full space-y-1.5 md:max-w-md">
                                <label className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 pl-1">Medical Condition</label>
                                <select 
                                    value={selectedDiseaseId} 
                                    onChange={e => setSelectedDiseaseId(Number(e.target.value))}
                                    className="w-full bg-white border border-slate-100 rounded-xl px-4 py-2.5 text-xs sm:text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#0870b4] text-slate-700"
                                >
                                    {diseases.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                            </div>

                            <button 
                                onClick={handleAddRecord}
                                disabled={adding}
                                className="w-full md:w-auto px-6 py-2.5 bg-[#0870b4] hover:bg-[#065b94] text-white font-extrabold uppercase tracking-widest text-[10px] sm:text-xs rounded-xl shadow-md shadow-[#0870b4]/10 transition-all duration-200 flex items-center justify-center gap-1.5 h-[41px]"
                            >
                                <Plus size={14} className="stroke-[3.5]" />
                                {adding ? 'Adding...' : 'Add Record'}
                            </button>
                        </div>
                    </div>

                    {/* Active Positive Records Display (Clean, simple, outstanding UX) */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between pb-2 border-b border-slate-100/60 pl-1">
                            <span className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400">Reported Family History</span>
                            <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2.5 py-0.5 rounded-full">
                                {activeMembers.length} Affected Member(s)
                            </span>
                        </div>

                        {activeMembers.length === 0 ? (
                            // Pristine clean fallback state
                            <div className="bg-white rounded-[2rem] border border-slate-100 p-12 text-center shadow-[0_8px_30px_rgba(0,0,0,0.01)]">
                                <ShieldCheck className="mx-auto text-slate-200 w-12 h-12 mb-3 stroke-[1.5]" />
                                <h4 className="text-sm font-extrabold text-slate-700 mb-1">No significant family history reported</h4>
                                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                    All family members are currently marked as healthy or free of tracked conditions.
                                </p>
                            </div>
                        ) : (
                            // Clean Card Grid grouped by family relationship
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {activeMembers.map((member) => {
                                    // Find all positive diseases for this relationship
                                    const memberDiseases = diseases.filter(d => 
                                        d.attributes.values.some(v => v.relationship === member && v.status)
                                    );

                                    return (
                                        <div 
                                            key={member}
                                            className="bg-white border border-slate-100 rounded-3xl p-5 shadow-[0_8px_24px_rgba(8,112,180,0.015)] flex flex-col justify-between group hover:shadow-[0_12px_32px_rgba(8,112,180,0.03)] hover:-translate-y-0.5 transition-all duration-300 relative overflow-hidden"
                                        >
                                            {/* Colored left bar for beautiful visual branding */}
                                            <div className="absolute top-0 left-0 w-1 h-full bg-[#0870b4]" />

                                            <div className="space-y-4 pl-2">
                                                {/* Header Member Title */}
                                                <div className="flex items-center gap-2">
                                                    <div className="w-7 h-7 rounded-full bg-[#0870b4]/10 text-[#0870b4] flex items-center justify-center">
                                                        <Users size={12} className="stroke-[2.5]" />
                                                    </div>
                                                    <span className="text-sm font-extrabold uppercase tracking-wide text-slate-800">
                                                        {member}
                                                    </span>
                                                </div>

                                                {/* Wrap list of positive conditions */}
                                                <div className="flex flex-wrap gap-2">
                                                    {memberDiseases.map((disease) => (
                                                        <Tooltip 
                                                            key={disease.id}
                                                            title="Click x to remove this medical history record"
                                                            arrow
                                                            placement="top"
                                                        >
                                                            <div className="inline-flex items-center gap-1.5 pl-3.5 pr-2 py-1.5 rounded-full bg-[#0870b4]/5 text-[#0870b4] font-bold text-xs border border-[#0870b4]/10 group/tag hover:bg-rose-50 hover:text-rose-600 hover:border-rose-100 transition-all duration-200">
                                                                <span className="leading-none">{disease.name}</span>
                                                                <button 
                                                                    onClick={() => handleRemoveRecord(disease.id, member)}
                                                                    className="w-4.5 h-4.5 rounded-full bg-white text-[#0870b4] group-hover/tag:text-rose-600 flex items-center justify-center transition-colors shadow-sm"
                                                                >
                                                                    <X size={10} className="stroke-[3]" />
                                                                </button>
                                                            </div>
                                                        </Tooltip>
                                                    ))}
                                                </div>
                                            </div>

                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                </div>
            )}

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={() => setSnackbarOpen(false)} 
                    severity={snackbarSeverity} 
                    sx={{ width: '100%', fontFamily: 'Jost, sans-serif' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default FamilyHistory;
